import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../api/auth'

// 用户角色类型
export enum UserRole {
  READER = 'reader',
  WRITER = 'writer',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin'
}

// 用户接口
export interface User {
  id: string
  username: string
  email: string
  role: UserRole
  avatar?: string
  createdAt: string
}

// 转换API用户数据到本地User格式
const convertApiUser = (apiUser: any): User => ({
  id: apiUser.id,
  username: apiUser.username,
  email: apiUser.email,
  role: apiUser.role as UserRole,
  avatar: apiUser.avatar,
  createdAt: apiUser.created_at || apiUser.createdAt,
})


// 认证状态接口
interface AuthState {
  // 状态
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // 操作
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  refreshAccessToken: () => Promise<void>
  setTokens: (accessToken: string, refreshToken: string) => void
  clearAuth: () => void
  ssoCallback: (token: string) => Promise<void>
}

// 创建认证store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // 登录
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          // 第1步：登录获取access_token
          const response = await authApi.login({ username, password })
          const { access_token } = response
          
          // 第2步：先保存token，然后获取用户信息
          set({
            accessToken: access_token,
            isAuthenticated: true,
            isLoading: true
          })
          
          // 第3步：用token获取用户信息
          try {
            const apiUser = await authApi.getCurrentUser()
            set({
              user: convertApiUser(apiUser),
              isLoading: false,
              error: null
            })
          } catch {
            // 能登录但获取用户信息失败 -> 至少允许登录
            set({ isLoading: false, error: null })
          }
        } catch (error: any) {
          set({
            error: error.response?.data?.detail || '登录失败',
            isLoading: false,
            isAuthenticated: false
          })
          throw error
        }
      },

      // 登出
      logout: () => {
        // 调用登出API（可选）
        authApi.logout().catch(console.error)
        
        // 清除本地状态
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null
        })
      },

      // 检查认证状态
      checkAuth: async () => {
        const { accessToken } = get()
        
        if (!accessToken) {
          set({ isAuthenticated: false })
          return
        }

        set({ isLoading: true })
        try {
          // 验证token有效性（调用用户信息接口）
          const apiUser = await authApi.getCurrentUser()
          set({
            user: convertApiUser(apiUser),
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          // token无效，尝试刷新
          try {
            await get().refreshAccessToken()
          } catch {
            // 刷新失败，清除认证状态
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false
            })
          }
        }
      },

      // 刷新token
      refreshAccessToken: async () => {
        const { refreshToken } = get()
        
        if (!refreshToken) {
          throw new Error('没有刷新令牌')
        }

        try {
          const response = await authApi.refreshToken(refreshToken)
          const { access_token, refresh_token } = response
          
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true
          })
        } catch (error) {
          // 刷新失败，清除认证状态
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false
          })
          throw error
        }
      },

      // 设置tokens（用于单点登录）
      setTokens: (accessToken: string, refreshToken: string) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true
        })
        
        // 获取用户信息
        get().checkAuth()
      },

      // 清除认证状态
      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null
        })
      },

      // 单点登录回调
      ssoCallback: async (token: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.ssoCallback(token)
          const { access_token, refresh_token, user: apiUser } = response
          
          set({
            user: convertApiUser(apiUser),
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '单点登录失败'
          })
          throw error
        }
      }
    }),
    {
      name: 'auth-storage', // localStorage的key
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)